import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import backIcon from "../../assets/icons/backIcon.svg";
import pageStyles from "../../styles/pages/Templates.module.css";
import Button from "../../ui/buttons/Button";
import IconButton from "../../ui/buttons/IconButton";
import LoadingScreen from "../../ui/loadingScreen/LoadingScreen";
import { whatsappService } from "../../services/whatsapp.service";
import { extractApiErrorMessage } from "../../utils/apiError";
import { WHATSAPP_MANAGER_URL } from "../../utils/whatsappTemplateDraft";
import TemplatesToolbar from "../templates/TemplatesToolbar";
import WhatsAppTemplateGroupModal from "./WhatsAppTemplateGroupModal";
import WhatsAppTemplateGroupsTable, { UNGROUPED_GROUP_NAME } from "./WhatsAppTemplateGroupsTable";
import WhatsAppTemplatePanel, { REVIEW_HINT, connectionLabel, numberLabel } from "./WhatsAppTemplatePanel";
import WhatsAppTemplatesTable from "./WhatsAppTemplatesTable";

import styles from "./WhatsAppTemplates.module.css";

export { DELETE_RULE_HINT, REVIEW_HINT } from "./WhatsAppTemplatePanel";

export const TEMPLATES_INTRO_LINES = [
  "Las plantillas son mensajes que tu empresa prepara de antemano y Meta revisa.",
  "Son la única forma de escribirle por WhatsApp a alguien que no te habló en las últimas 24 horas, por ejemplo para un primer contacto o un seguimiento.",
  "Son de tu cuenta de WhatsApp Business: cada empresa tiene las suyas. Los grupos son de MaqMailing, para ordenarlas.",
];
export const OPEN_MANAGER_LABEL = "Abrir WhatsApp Manager ↗";
export const REFRESH_LABEL = "Actualizar estados";
export const NEW_GROUP_BUTTON_LABEL = "Nuevo grupo";
export const NEW_TEMPLATE_LABEL = "Nueva Plantilla";
export const NO_TEMPLATES_YET = "Todavía no hay plantillas en tu cuenta de WhatsApp Business.";
export const NO_TEMPLATES_IN_GROUP = "Este grupo todavía no tiene plantillas.";
export const NO_SEARCH_RESULTS = "Ninguna plantilla coincide con la búsqueda.";
export const NO_GROUP_RESULTS = "Ningún grupo coincide con la búsqueda.";
export const CONNECT_FIRST_MESSAGE =
  "Para usar plantillas primero hay que conectar un número de WhatsApp Business.";
export const CONNECT_NUMBER_PATH = "/templates?canal=whatsapp&configuracion=cuentas-linkeadas";
export const SECTION_TEMPLATES = "op1";
export const SECTION_ATTACHMENTS = "op2";
export const SECTION_WHATSAPP = "op3";
export const MAIL_TEMPLATES_PATH = "/templates";

const BackIcon = <img src={backIcon} alt="" style={{ width: 32, height: 32, display: "block" }} />;

export const groupConnectionsByWaba = (connections) =>
  connections.reduce((groups, connection) => {
    const wabaId = connection.waba_id || `connection-${connection.id}`;
    const group = groups.find((candidate) => candidate.wabaId === wabaId);
    if (group) {
      group.connections.push(connection);
      return groups;
    }
    return [...groups, { wabaId, connections: [connection] }];
  }, []);

export const groupSummaries = (groups, templates) => [
  ...groups.map((group) => ({ ...group, templates: templates.filter((template) => template.group?.id === group.id) })),
  { id: null, name: UNGROUPED_GROUP_NAME, templates: templates.filter((template) => !template.group) },
];

const templatesErrorMessage = (failures, groupCount) => {
  if (!failures.length) return null;
  if (groupCount === 1) {
    const [{ detail }] = failures;
    return detail
      ? `No se pudieron consultar las plantillas en Meta: ${detail}`
      : "No se pudieron cargar las plantillas de WhatsApp.";
  }
  const described = failures.map(({ label, detail }) => (detail ? `${label}: ${detail}` : label));
  return `No se pudieron consultar en Meta las plantillas de ${described.join("; ")}`;
};

const matchesSearch = (template, query) =>
  !query ||
  [
    template.name,
    template.body_text,
    ...(template.numbers || []).flatMap((connection) => [
      numberLabel(connection),
      connection.mail_account_name,
      connection.mail_account_email,
    ]),
  ].some((value) => (value || "").toLowerCase().includes(query));

const inGroup = (template, group) => (group.id == null ? !template.group : template.group?.id === group.id);

export const connectionsByAccount = (connections) => {
  const representatives = new Map();
  connections.forEach((connection) => {
    if (!representatives.has(connection.account)) representatives.set(connection.account, connection);
  });
  return [...representatives.values()];
};

const sameTemplate = (left, right) =>
  left.name === right.name && left.connection?.waba_id === right.connection?.waba_id;

export default function WhatsAppTemplatesManager() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [connections, setConnections] = useState([]);
  const [connectionsLoading, setConnectionsLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesLoaded, setTemplatesLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [search, setSearch] = useState("");
  const [groups, setGroups] = useState([]);
  const [openGroup, setOpenGroup] = useState(null);
  const [groupModal, setGroupModal] = useState(null);
  const [groupBusy, setGroupBusy] = useState(false);
  const [panel, setPanel] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [tagsByAccount, setTagsByAccount] = useState({});
  const latestTemplatesRequestRef = useRef(0);
  const settingsWereOpenRef = useRef(false);
  const settingsOpen = Boolean(searchParams.get("configuracion"));
  const requestedId = searchParams.get("numero");
  const wabaGroups = useMemo(() => groupConnectionsByWaba(connections), [connections]);
  const defaultConnection =
    connections.find((connection) => String(connection.id) === String(requestedId)) ?? connections[0] ?? null;

  const loadConnections = useCallback(() => {
    let mounted = true;
    whatsappService
      .listAccounts()
      .then(({ results }) => {
        if (mounted) setConnections(results);
      })
      .catch((requestError) => {
        if (mounted) {
          setError(extractApiErrorMessage(requestError) || "No se pudieron cargar los números de WhatsApp.");
        }
      })
      .finally(() => {
        if (mounted) setConnectionsLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => loadConnections(), [loadConnections]);

  useEffect(() => {
    const settingsJustClosed = settingsWereOpenRef.current && !settingsOpen;
    settingsWereOpenRef.current = settingsOpen;
    if (settingsJustClosed) return loadConnections();
    return undefined;
  }, [settingsOpen, loadConnections]);

  const loadTemplates = useCallback(async () => {
    if (!wabaGroups.length) return;
    const requestId = latestTemplatesRequestRef.current + 1;
    latestTemplatesRequestRef.current = requestId;
    setTemplatesLoading(true);
    setError(null);
    const results = await Promise.allSettled(
      wabaGroups.map((group) => whatsappService.listTemplates(group.connections[0].id, { all: true })),
    );
    if (latestTemplatesRequestRef.current !== requestId) return;
    const rows = [];
    const failures = [];
    results.forEach((result, index) => {
      const group = wabaGroups[index];
      if (result.status === "fulfilled") {
        rows.push(
          ...result.value.results.map((template) => ({
            ...template,
            connection: group.connections[0],
            numbers: group.connections,
          })),
        );
      } else {
        failures.push({
          label: connectionLabel(group.connections[0]),
          detail: extractApiErrorMessage(result.reason),
        });
      }
    });
    setTemplates(rows);
    setError(templatesErrorMessage(failures, wabaGroups.length));
    setTemplatesLoading(false);
    setTemplatesLoaded(true);
  }, [wabaGroups]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const loadGroups = useCallback(async () => {
    const representatives = connectionsByAccount(connections);
    const loaded = await Promise.all(
      representatives.map(async (connection) => {
        try {
          const accountGroups = await whatsappService.listTemplateGroups(connection.id);
          return accountGroups.map((group) => ({
            ...group,
            accountId: connection.account,
            connectionId: connection.id,
          }));
        } catch {
          return [];
        }
      }),
    );
    const merged = loaded.flat();
    setGroups(merged);
    return merged;
  }, [connections]);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  useEffect(() => {
    let mounted = true;
    const representatives = connectionsByAccount(connections);
    if (!representatives.length) return undefined;
    Promise.all(
      representatives.map((connection) =>
        whatsappService
          .listTags(connection.id)
          .then((results) => [connection.account, results])
          .catch(() => [connection.account, []]),
      ),
    ).then((entries) => {
      if (mounted) setTagsByAccount(Object.fromEntries(entries));
    });
    return () => {
      mounted = false;
    };
  }, [connections]);

  useEffect(() => {
    if (openGroup?.id != null && !groups.some((group) => group.id === openGroup.id)) {
      setOpenGroup(null);
    }
  }, [groups, openGroup]);

  const showGroup = (group) => {
    setSearch("");
    setNotice(null);
    setOpenGroup(
      group
        ? {
            id: group.id,
            name: group.name,
            accountId: group.accountId ?? null,
            connectionId: group.connectionId ?? null,
          }
        : null,
    );
  };

  const handleCreate = async (payload, connection) => {
    setSubmitting(true);
    try {
      const created = await whatsappService.createTemplate(connection.id, payload);
      setPanel(null);
      setNotice(`La plantilla ${created?.name || payload.name} quedó en revisión de Meta. ${REVIEW_HINT}`);
      await loadTemplates();
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (template, payload) => {
    setSubmitting(true);
    try {
      await whatsappService.updateTemplate(template.connection.id, template.name, {
        ...payload,
        language: template.language,
      });
      setPanel(null);
      setNotice(`La plantilla ${template.name} volvió a revisión de Meta. ${REVIEW_HINT}`);
      await loadTemplates();
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (template) => {
    setDeleting(true);
    setError(null);
    try {
      await whatsappService.deleteTemplate(template.connection.id, template.name);
      setPanel(null);
      setNotice(`Borraste la plantilla ${template.name}.`);
      await loadTemplates();
    } catch (requestError) {
      setPanel(null);
      setError(extractApiErrorMessage(requestError) || "No se pudo borrar la plantilla.");
    } finally {
      setDeleting(false);
    }
  };

  const handleSetGroup = async (template, groupId) => {
    setError(null);
    try {
      const { group } = await whatsappService.setTemplateGroup(template.connection.id, template.name, groupId);
      setTemplates((previous) => previous.map((row) => (sameTemplate(row, template) ? { ...row, group } : row)));
      setPanel((previous) =>
        previous?.template && sameTemplate(previous.template, template)
          ? { template: { ...previous.template, group } }
          : previous,
      );
    } catch (requestError) {
      setError(extractApiErrorMessage(requestError) || "No se pudo cambiar el grupo de la plantilla.");
    }
  };

  const handleCreateGroup = async (name, connectionId = defaultConnection?.id) => {
    const group = await whatsappService.createTemplateGroup(connectionId, name);
    await loadGroups();
    return group;
  };

  const handleSaveGroup = async (name) => {
    setGroupBusy(true);
    try {
      if (groupModal?.group) {
        const { group } = groupModal;
        const renamed = await whatsappService.renameTemplateGroup(group.connectionId, group.id, name);
        await loadGroups();
        setTemplates((previous) =>
          previous.map((row) => (row.group?.id === renamed.id ? { ...row, group: renamed } : row)),
        );
        setOpenGroup((previous) => (previous?.id === renamed.id ? { ...previous, name: renamed.name } : previous));
      } else {
        await handleCreateGroup(name);
      }
      setGroupModal(null);
    } finally {
      setGroupBusy(false);
    }
  };

  const handleDeleteGroup = async (group) => {
    setGroupBusy(true);
    try {
      await whatsappService.deleteTemplateGroup(group.connectionId, group.id);
      setTemplates((previous) =>
        previous.map((row) => (row.group?.id === group.id ? { ...row, group: null } : row)),
      );
      await loadGroups();
      setGroupModal(null);
      setNotice(`Borraste el grupo ${group.name}; sus plantillas quedaron sin agrupar.`);
    } finally {
      setGroupBusy(false);
    }
  };

  const openNew = () => {
    if (!connections.length) {
      navigate(CONNECT_NUMBER_PATH);
      return;
    }
    setNotice(null);
    if (openGroup) {
      setPanel({ template: null });
    } else {
      setGroupModal({ group: null });
    }
  };

  const openWhatsAppManager = () => window.open(WHATSAPP_MANAGER_URL, "_blank", "noopener,noreferrer");

  const handleSectionChange = (section) => {
    if (section === SECTION_TEMPLATES) navigate(MAIL_TEMPLATES_PATH);
    if (section === SECTION_ATTACHMENTS) navigate(MAIL_TEMPLATES_PATH, { state: { viewMode: "attachments" } });
  };

  if (connectionsLoading) {
    return <LoadingScreen embedded mainText="Cargando plantillas..." subText="" />;
  }

  const query = search.trim().toLowerCase();
  const summaries = groupSummaries(groups, templates);
  const groupConnection = openGroup?.connectionId
    ? connections.find((connection) => connection.id === openGroup.connectionId) ?? null
    : null;
  const panelConnection = panel?.template?.connection ?? groupConnection ?? defaultConnection;
  const panelConnections =
    openGroup?.accountId != null
      ? connections.filter((connection) => connection.account === openGroup.accountId)
      : connections;
  const visibleGroups = summaries.filter((group) => !query || group.name.toLowerCase().includes(query));
  const groupTemplates = openGroup
    ? templates.filter((template) => inGroup(template, openGroup) && matchesSearch(template, query))
    : [];

  const renderContent = () => {
    if (connections.length === 0) {
      return (
        <p className={pageStyles.emptyTemplateFilesText}>
          {CONNECT_FIRST_MESSAGE} <Link to={CONNECT_NUMBER_PATH}>Conectar un número</Link>
        </p>
      );
    }
    if (templatesLoading || !templatesLoaded) {
      return <p className={pageStyles.emptyTemplateFilesText}>Consultando a Meta...</p>;
    }
    if (!openGroup) {
      if (visibleGroups.length === 0) {
        return <p className={pageStyles.emptyTemplateFilesText}>{NO_GROUP_RESULTS}</p>;
      }
      return (
        <WhatsAppTemplateGroupsTable
          groups={visibleGroups}
          onOpen={showGroup}
          onEdit={(group) => setGroupModal({ group })}
        />
      );
    }
    if (groupTemplates.length === 0) {
      const emptyMessage = query ? NO_SEARCH_RESULTS : openGroup.id == null ? NO_TEMPLATES_YET : NO_TEMPLATES_IN_GROUP;
      return <p className={pageStyles.emptyTemplateFilesText}>{emptyMessage}</p>;
    }
    return (
      <WhatsAppTemplatesTable
        rows={groupTemplates}
        showSections={false}
        onOpen={(template) => setPanel({ template })}
      />
    );
  };

  return (
    <div className={pageStyles.templates}>
      <div className={pageStyles.templatesContent}>
        <div className={pageStyles.templatesBody}>
          <div className={pageStyles.frameParent}>
            <div className={pageStyles.templatesWrapper}>
              {openGroup && (
                <IconButton
                  tone="neutral"
                  size="md"
                  icon={BackIcon}
                  onClick={(event) => {
                    event.stopPropagation();
                    showGroup(null);
                  }}
                  aria-label="volver"
                />
              )}
              <div className={pageStyles.div}>{openGroup ? openGroup.name : "Templates"}</div>
            </div>

            <div className={pageStyles.frameParent}>
              <TemplatesToolbar
                key={openGroup ? `group-${openGroup.id ?? "ungrouped"}` : "groups"}
                selectedFilter={SECTION_WHATSAPP}
                onSearchChange={setSearch}
                onFilterChange={handleSectionChange}
                onNewWhatsAppTemplate={openNew}
                newWhatsAppLabel={openGroup ? NEW_TEMPLATE_LABEL : NEW_GROUP_BUTTON_LABEL}
              />

              {openGroup ? (
                <div className={styles.breadcrumb}>Templates / {openGroup.name}</div>
              ) : (
                <div className={styles.intro}>
                  {TEMPLATES_INTRO_LINES.map((line) => (
                    <p className={styles.subtitle} key={line}>
                      {line}
                    </p>
                  ))}
                </div>
              )}
              <div className={styles.introActions}>
                <Button variant="secondary" tone="blue" height={40} onClick={openWhatsAppManager}>
                  {OPEN_MANAGER_LABEL}
                </Button>
                {connections.length > 0 && (
                  <Button
                    variant="secondary"
                    tone="blue"
                    height={40}
                    onClick={loadTemplates}
                    disabled={templatesLoading}
                  >
                    {REFRESH_LABEL}
                  </Button>
                )}
              </div>

              {error && (
                <p className={styles.error} role="alert">
                  {error}
                </p>
              )}
              {notice && (
                <p className={styles.notice} role="status">
                  {notice}
                </p>
              )}

              {renderContent()}
            </div>
          </div>
        </div>
      </div>

      {panel && (
        <WhatsAppTemplatePanel
          connections={panelConnections}
          defaultConnection={panelConnection}
          template={panel.template}
          tagsByAccount={tagsByAccount}
          groups={groups}
          defaultGroupId={openGroup?.id ?? null}
          onClose={() => setPanel(null)}
          onSubmit={handleCreate}
          onUpdate={handleUpdate}
          onDelete={handleDelete}
          onSetGroup={handleSetGroup}
          onCreateGroup={(name, formConnection) => handleCreateGroup(name, (formConnection ?? panelConnection)?.id)}
          submitting={submitting}
          deleting={deleting}
        />
      )}

      {groupModal && (
        <WhatsAppTemplateGroupModal
          group={groupModal.group}
          busy={groupBusy}
          onClose={() => setGroupModal(null)}
          onSave={handleSaveGroup}
          onDelete={handleDeleteGroup}
        />
      )}
    </div>
  );
}
